import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMenu, fetchSpecials } from "../lib/api";
import { useSeo } from "../components/site/useSeo";
import { Reveal } from "../components/site/Reveal";
import { Flame, Star, Leaf, Sparkles } from "lucide-react";

const Indicator = ({ icon: Icon, label, cls }) => (
  <span className={`inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-widest font-semibold ${cls}`}>
    <Icon size={12} /> {label}
  </span>
);

const MenuItem = ({ item }) => (
  <div className="flex justify-between gap-6 py-5 border-b border-hb-linelight" data-testid="menu-item">
    <div className="flex-1">
      <div className="flex items-baseline gap-3 flex-wrap">
        <h4 className="font-serif text-2xl text-hb-ink">{item.name}</h4>
        <div className="flex gap-3">
          {item.spicy && <Indicator icon={Flame} label="Spicy" cls="text-hb-amber" />}
          {item.favorite && <Indicator icon={Star} label="Favorite" cls="text-hb-brass" />}
          {item.gluten_conscious && <Indicator icon={Leaf} label="GF Conscious" cls="text-green-700" />}
          {item.is_new && <Indicator icon={Sparkles} label="New" cls="text-hb-barnwood" />}
        </div>
      </div>
      {item.description && <p className="text-hb-mutedlight text-sm mt-1.5 max-w-lg leading-relaxed">{item.description}</p>}
    </div>
    {item.price && <div className="font-serif text-xl text-hb-ink whitespace-nowrap pt-1">{item.price}</div>}
  </div>
);

export default function MenuPage() {
  const { data: menuData } = useQuery({ queryKey: ["menu"], queryFn: fetchMenu, staleTime: 0, refetchOnMount: "always" });
  const { data: specialsData } = useQuery({ queryKey: ["specials"], queryFn: fetchSpecials, staleTime: 0, refetchOnMount: "always" });
  const [active, setActive] = useState(null);

  useSeo({
    title: "Menu | Hackberry Home — Southern & Cajun Food in Ben Wheeler, TX",
    description: "Explore the Hackberry Home menu — Southern favorites, Cajun & Louisiana dishes, scratch-made sides, and homemade desserts in Ben Wheeler, Texas.",
    path: "/menu",
  });

  const categories = useMemo(() => (menuData?.categories || []).filter((c) => (c.items || []).length), [menuData]);
  const specials = specialsData?.specials || [];

  useEffect(() => {
    if (categories.length && !active) setActive(categories[0].id);
  }, [categories, active]);

  const scrollToCat = (id) => {
    setActive(id);
    const el = document.getElementById(`cat-${id}`);
    if (!el) return;
    if (window.lenis) {
      window.lenis.scrollTo(el, { offset: -140 });
    } else {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 140, behavior: "smooth" });
    }
  };

  return (
    <div className="bg-hb-cream min-h-screen">
      {/* header */}
      <header className="pt-32 pb-14 px-5 md:px-10 max-w-6xl mx-auto text-center">
        <Reveal><p className="eyebrow text-hb-barnwood mb-4">Scratch Made Daily</p></Reveal>
        <Reveal delay={0.05}><h1 className="font-serif text-6xl md:text-8xl text-hb-ink leading-none">Our Menu</h1></Reveal>
        <Reveal delay={0.1}><p className="text-hb-mutedlight mt-6 max-w-xl mx-auto">Southern comfort with a little Louisiana soul — scratch-made and served fresh daily, 6:00 AM – 3:00 PM.</p></Reveal>
        <div className="hb-line w-24 mx-auto mt-8" />
      </header>

      {/* Today's Specials */}
      {specials.length > 0 && (
        <section className="max-w-4xl mx-auto px-5 md:px-10 mb-14" data-testid="todays-specials">
          <div className="border-2 border-hb-ink bg-hb-creamsurface p-8 md:p-10">
            <p className="eyebrow text-hb-amber mb-2">Today's Specials</p>
            <h2 className="font-serif text-4xl text-hb-ink mb-6">Fresh From the Kitchen</h2>
            <div className="space-y-1">
              {specials.map((s) => (
                <div key={s.id} className="flex justify-between gap-4 py-3 border-b border-hb-linelight last:border-0">
                  <div>
                    <h4 className="font-serif text-2xl text-hb-ink">{s.name}</h4>
                    {s.description && <p className="text-hb-mutedlight text-sm mt-1">{s.description}</p>}
                  </div>
                  {s.price && <span className="font-serif text-xl text-hb-ink">{s.price}</span>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* sticky category jump bar */}
      <div className="sticky top-[72px] z-30 bg-hb-cream/95 backdrop-blur-md border-y border-hb-linelight" data-testid="category-nav">
        <div className="max-w-6xl mx-auto px-5 md:px-10 flex gap-2 overflow-x-auto no-scrollbar py-3">
          {categories.map((c) => (
            <button
              key={c.id}
              onClick={() => scrollToCat(c.id)}
              data-testid={`category-tab-${c.id}`}
              className={`whitespace-nowrap px-4 py-2 eyebrow text-[0.65rem] border transition-colors ${
                active === c.id ? "bg-hb-ink text-hb-cream border-hb-ink" : "border-hb-linelight text-hb-mutedlight hover:border-hb-ink hover:text-hb-ink"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* categories */}
      <div className="max-w-4xl mx-auto px-5 md:px-10 py-16">
        {categories.map((cat, ci) => (
          <section key={cat.id} id={`cat-${cat.id}`} className="mb-20 scroll-mt-40" data-testid={`menu-category-${cat.id}`}>
            <div className="flex items-center gap-5 mb-6">
              <h2 className="font-serif text-4xl md:text-5xl text-hb-ink">{cat.name}</h2>
              <div className="flex-1 h-px bg-hb-linelight" />
              <span className="font-serif text-hb-linelight text-3xl">{String(ci + 1).padStart(2, "0")}</span>
            </div>
            {cat.note && <p className="text-hb-mutedlight text-sm italic -mt-2 mb-6 max-w-2xl leading-relaxed">{cat.note}</p>}
            <div>
              {(cat.items || []).map((item) => <MenuItem key={item.id} item={item} />)}
            </div>
          </section>
        ))}
        {!categories.length && <p className="text-center text-hb-mutedlight py-20">Menu is being prepared. Please check back soon.</p>}
      </div>

      {/* legend */}
      <div className="max-w-4xl mx-auto px-5 md:px-10 pb-24">
        <div className="border-t border-hb-linelight pt-6 flex flex-wrap gap-6">
          <Indicator icon={Flame} label="Spicy" cls="text-hb-amber" />
          <Indicator icon={Star} label="Customer Favorite" cls="text-hb-brass" />
          <Indicator icon={Leaf} label="Gluten Conscious" cls="text-green-700" />
          <Indicator icon={Sparkles} label="New" cls="text-hb-barnwood" />
        </div>
      </div>
    </div>
  );
}
